import { useQuery } from "@tanstack/react-query";
import { Loader2, Video } from "@fg/icons";
import { connectParityApi, type MeetingDriveRecording } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { formatDuration } from "@/lib/drive";
import { cn } from "@/lib/utils";
import { DriveFileCard } from "../files/DriveFileCard";

/** A meeting's Drive recordings. Refreshed by the hub's meeting.recording.added / .saved events. */
export function useMeetingRecordings(meetingId: string | null | undefined, enabled = true) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["meeting-recordings", meetingId, token],
        queryFn: () => connectParityApi.listMeetingRecordings(meetingId!, token),
        enabled: Boolean(meetingId && token && enabled),
        staleTime: 30_000,
        retry: 1
    });
}

/** One recording as a file card (Open plays it in-app; Download / Open in Drive / Copy link). */
export function RecordingCard({ recording, className }: { recording: MeetingDriveRecording; className?: string }) {
    const when = new Date(recording.createdAt);
    return (
        <DriveFileCard
            className={className}
            file={{
                id: recording.driveItemId,
                name: recording.fileName,
                size: recording.sizeBytes,
                type: "video/webm",
                owner: ""
            }}
            detail={[
                `Recording · ${formatDuration(recording.durationSeconds)}`,
                recording.recordedByName ? `by ${recording.recordedByName}` : null,
                when.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
            ].filter(Boolean).join(" · ")}
        />
    );
}

/**
 * The "Recordings" section of a meeting (calendar details). Renders nothing
 * until there is at least one, so meetings without recordings stay clean.
 */
export function MeetingRecordingsSection({ meetingId, className }: { meetingId: string; className?: string }) {
    const recordings = useMeetingRecordings(meetingId);
    if (recordings.isPending && recordings.fetchStatus === "fetching") {
        return (
            <p className={cn("flex items-center gap-2 text-xs text-muted-foreground", className)}>
                <Loader2 className="h-3 w-3 animate-spin" /> Checking for recordings…
            </p>
        );
    }
    const rows = recordings.data ?? [];
    if (rows.length === 0) return null;
    return (
        <section aria-label="Recordings" className={cn("space-y-1.5", className)}>
            <h3 className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                <Video className="h-3.5 w-3.5" /> Recordings · {rows.length}
            </h3>
            <div className="space-y-1.5">
                {rows.map((r) => <RecordingCard key={r.id} recording={r} />)}
            </div>
        </section>
    );
}
